import { useEffect, useMemo, useRef, useState } from "react";
import { 
    Autocomplete, 
    AutocompleteInputChangeReason, 
    Box, 
    Button, 
    debounce, 
    Divider, 
    FormControl, 
    FormHelperText, 
    ListItem, 
    Table, 
    TableBody, 
    TableCell, 
    TableHead, 
    TableRow, 
    Typography 
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { List } from "react-movable";
import { Control, Controller, FormState, UseFormClearErrors, UseFormGetValues, UseFormReset } from "react-hook-form";
import { isNil, isNull } from "lodash";

import { 
    flexAlignCentre, 
    flexAllEnd 
} from "../../../styles/mui/styles/display";
import { FormInputDropdown } from "../../../components/FormInputDropdown";
import { colors } from "../../../styles/colors";
import fontWeight from "../../../styles/mui/fontWeight";
import { 
    DiseaseType, 
    ICD10CodeType, 
    ICD10DuplicateError, 
    ICD10Text, 
    ICD10Title, 
    IcdCodeForReAuth, 
    IcdCodeType, 
    IcdDiseaseType, 
    InfoTable 
} from "../../../constants/ICD10";
import { arrayMove, getValue, length } from "../../../utils";
import {
    AutocompleteTabelCell,
    AutocompleteUI, 
    CodeTypeDropDownWrap, 
    CodeTypeTabelCell, 
    DeleteButton, 
    DescriptionTabelCell, 
    FaGripLinesIcon, 
    FaRegTrashAltIcon, 
    FaSearchIcon, 
    NextStepButton,
    ReauthOpacity, 
    RemoveRowDeleteButton, 
    StyledPopperCheckBoxAutocomplete, 
    SummaryItemContainer, 
    tableCellColor, 
    tableCellNoColor, 
    TableHeadStyle 
} from "../../PriorAuthWorkFlow/styles/style";
import FormInputFieldIcon from "../../../components/FormPriorAuthReAuth/FormInputFieldIcon";
import { rules } from "../../../utils/validation/Validation";
import { ReAuthTabData } from "../../../models/TabSessionStorage";
import { FormInputText } from "../../../components/FormPriorAuthReAuth/FormInputText";
import { MyAccountModel } from "../../../models/MyAccount";
import { updateTabStatus } from "../../../constants/TabSessionStorage";
import { IcdCodeForReAuthPayload } from "../../../models/Patient";
import { ReAuthDefaultReferralDetails } from "../../../models/Referral";

export interface PropsFromState {
    append: any;
    clearErrors: UseFormClearErrors<ReAuthTabData>;
    control: Control<ReAuthTabData, any>;
    diseaseTypeResp: DiseaseType[];
    fields: InfoTable[];
    getValues: UseFormGetValues<any>;
    icdCodeDataType: IcdCodeType[];
    icdvalues: ReAuthTabData;
    remove: (id: number) => void;
    reset: UseFormReset<ReAuthTabData>;
    setValue: (id: any, value: any, boolean: any) => void;
    watch?: any;
    formState?: FormState<any>;
    loggedInUserState: MyAccountModel;
    icdCodeForReAuthState?: IcdCodeForReAuth;
    reAuthReferralDetailState: ReAuthDefaultReferralDetails;
    tabSessionStorageReAuthState: ReAuthTabData;
  }

  export interface PropsFromDispatch {
    getDiseaseTypeData: (diseaseIdentifier: string) => void;
    getIcdeCodeDataType: () => void;
    getIcdCodeForReAuth: (payload: IcdCodeForReAuthPayload) => void;
  }

  type AllProps = PropsFromState & PropsFromDispatch;

  const ICD10: React.FC<AllProps> = ({
    append,
    clearErrors,
    control,
    diseaseTypeResp,
    fields,
    getDiseaseTypeData,
    getIcdeCodeDataType,
    getValues,
    icdCodeDataType,
    icdvalues,
    remove,
    reset,
    setValue,
    watch,
    formState,
    getIcdCodeForReAuth,
    icdCodeForReAuthState,
    reAuthReferralDetailState,
    tabSessionStorageReAuthState
  }: AllProps) => {
    const [items, setItems] = useState<InfoTable[]>(fields);
    const [codeType, setCodeType] = useState<IcdCodeType[]>([]);
    const [removeFlag, setRemoveFlag] = useState<boolean>(false);
    const [icdDiseaseTypeResp, setDiseaseTypeResp] = useState<IcdDiseaseType[]>([]);
    const [dragged, setDragged] = useState<boolean>(false);
    const [duplicateIndexes, setDuplicateIndexes] = useState<number[]>([]);

      useEffect(() => {
          if (getValue(tabSessionStorageReAuthState, "diagnosisCode", "").length < 1) {
              getIcdeCodeDataType();
              setItems(fields);
              getIcdCodeForReAuth({
                  id: reAuthReferralDetailState.referralId,
                  typeId: 1,
              });
          }
      }, []);

      useEffect(() => {
          if (
              icdCodeForReAuthState &&
              Array.isArray(icdCodeForReAuthState.icdCodesInfoDto) &&
              icdCodeForReAuthState.icdCodesInfoDto.length > 0 
          ) {

              const seen = new Set<string>();
              const uniqueDtos = [...icdCodeForReAuthState.icdCodesInfoDto]
                  .sort((a: any, b: any) => Number(b.diagnosisTypeId === 1) - Number(a.diagnosisTypeId === 1))
                  .filter((c: any) => {
                  const codeKey = String(c.diagnosisCode ?? "").trim().toUpperCase();
                  const key = codeKey || (isNil(c.icdDiagnosisCodeId) ? "" : `id:${c.icdDiagnosisCodeId}`);
                  if (key && seen.has(key)) {
                      return false;
                  }
                  seen.add(key);
                  return true;
              });
              const mapped: InfoTable[] = uniqueDtos.map((c: any) => ({
                  diagnosisTypeId: c.diagnosisTypeId ?? 2,
                  diagnosisCode: c.diagnosisCode ?? "",
                  diagnosisCodeDescription: c.diagnosisCodeDescription ?? "",
                  icdDiagnosisCodeId: c.icdDiagnosisCodeId ?? null,
                  diagnosisType: (c.diagnosisTypeId === 1 ? "PRIMARY" : "SECONDARY"),
                  referralIcdCodeId: c.referralIcdCodeId ?? undefined,
              }));
              setItems(mapped);
              reset({
                  ...getValues(),
                  diagnosisCode: mapped,
              });
          }
      }, [icdCodeForReAuthState]);

    useEffect(() => {
        if (
          diseaseTypeResp &&
          diseaseTypeResp.length > 0 &&
          diseaseTypeResp[0].icdDiagnosisCodeId !== 0
        ) {
          const newArray = diseaseTypeResp.map(
            ({ icdDiagnosisCodeId, icdCode, icdDescription }) => ({
              diagnosisCode: icdCode,
              diagnosisCodeDescription: icdDescription,
              icdDiagnosisCodeId: icdDiagnosisCodeId,
            })
          );
          setDiseaseTypeResp(newArray);
        } else {
          setDiseaseTypeResp([]);
        }
      }, [diseaseTypeResp]);

    useEffect(() => {
        setCodeType(icdCodeDataType);
    }, [icdCodeDataType, fields]);

    useEffect(() => {
        setItems(fields);
        setDiseaseTypeResp([]);
      }, [fields]);
    
    useEffect(() => {
    if (removeFlag === true) {
        if (items.length > 0 && items[0].diagnosisTypeId === 2) {
            items[0].diagnosisTypeId = 1;
            setValue(`diagnosisCode.${0}.diagnosisTypeId`, 1, true);
            setValue(`diagnosisCode.${0}.diagnosisType`, 'PRIMARY', true);
            setDiseaseTypeResp([]);
            reset({ ...getValues(), diagnosisCode: items });
        }
        }
    }, [removeFlag, items, watch("diagnosisCode")]);

    useEffect(() => {
        if (!dragged) {
          const itemsData: InfoTable[] = items;
          setItems([]);
          setItems(itemsData);
        }
      }, [dragged]);

    useEffect(() => {
        if (getValue(icdvalues, "diagnosisCode", "").length > 0) {
            setItems(icdvalues.diagnosisCode);
            setValue("diagnosisCode", icdvalues.diagnosisCode, {
                shouldValidate: false,
                shouldDirty: false,
                shouldTouch: false
            });
        }
        reset({
          ...getValues(),
          reAuthTabStatus: updateTabStatus(
            2,
            getValues("reAuthTabStatus")
          ),
        });
    }, []);

    const sortableListRef = useRef<any>(null);

    const handleDragStart = (index: any) => {
        sortableListRef.current = index;
    };

    const handleDraggableClick = (event: any, index: any) => {
        const clickedCellIndex = event.currentTarget.cellIndex;
        if (clickedCellIndex !== 0) {
            event.stopPropagation();
        } else {
            handleDragStart(index);
        }
    };
    
    const handleArrayMove = (firstIndex: any, secIndex: any) => {
        setDuplicateIndexes([]);
        const newData = arrayMove(items, firstIndex, secIndex);
        newData.forEach((item, idx) => {
            if (idx === 0) {
                item.diagnosisTypeId = 1;
                item.diagnosisType = "PRIMARY";
            } else {
                item.diagnosisTypeId = 2;
                item.diagnosisType = "SECONDARY";
            }
        });
        reset({ ...getValues(), diagnosisCode: newData });
        setItems(newData);
    };

    const handleDiseaseTypeData = (
        event: React.SyntheticEvent<Element, Event>,
        value: string,
        reason: AutocompleteInputChangeReason
      ) => {
        if (!isNil(value) && !isNil(event)) {
          if (value.length >= 2 && reason === "input") {
            getDiseaseTypeData(value);
          }
          if (value.length === 0) {
            setDiseaseTypeResp([]);
          }
        }
    };

    const debouncedSearch = useMemo(
        () =>
          debounce((event, value, reason) => {
            handleDiseaseTypeData(event, value, reason);
          }, 500),
        []
    );

    const handleInputChange = (
        event: React.SyntheticEvent<Element, Event>,
        value: string,
        reason: AutocompleteInputChangeReason
      ) => {
        debouncedSearch(event, value, reason);
    };

    const diseaseCode = (disease: string) => {
        const diseaseCode = disease.split("-");
        return diseaseCode[0];
    };

    const handleDiseaseTypeChange = (
        _event: any,
        value: any,
        diseaseId: any,
        diagnosisCode: any,
        index: any
      ) => {
        const normalize = (code: any) =>
          String(code ?? "").split("-")[0].trim().toUpperCase();
        const existingRows: InfoTable[] = getValues("diagnosisCode") ?? items;
        const isDuplicate =
          !isNil(value) &&
          existingRows.some(
            (item, i) =>
              i !== Number(index) &&
              ((!isNil(item.icdDiagnosisCodeId) &&
                String(item.icdDiagnosisCodeId) === String(value.icdDiagnosisCodeId)) ||
                (normalize(item.diagnosisCode) !== "" &&
                  normalize(item.diagnosisCode) === normalize(value.diagnosisCode)))
          );
        if (isDuplicate) {
          value = null;
          setDuplicateIndexes((prev) => [...prev.filter((i) => i !== Number(index)), Number(index)]);
        } else {
          setDuplicateIndexes((prev) => prev.filter((i) => i !== Number(index)));
        }
        const itemList = items;
        itemList[index].icdDiagnosisCodeId =
          value !== null ? value.icdDiagnosisCodeId : "";
        itemList[index].diagnosisCode = value !== null ? value.diagnosisCode : "";
        itemList[index].diagnosisCodeDescription =
          value !== null ? value.diagnosisCodeDescription : "";
        setItems(itemList);
        setValue(diagnosisCode, value !== null ? value.diagnosisCode : "", true);
        setValue(diseaseId, value !== null ? value.icdDiagnosisCodeId : "", true);
    
        if (isNull(value)) {
          setValue(`diagnosisCode.${index}.diagnosisCodeDescription`, "", true);
        } else {
          const icd10Description = icdDiseaseTypeResp.filter(
            (val) => val.diagnosisCode === value.diagnosisCode
          );
          setValue(
            `diagnosisCode.${index}.diagnosisCodeDescription`,
            icd10Description[0].diagnosisCodeDescription,
            true
          );
        }
        clearErrors(diseaseId);
        setDiseaseTypeResp([]);
    };

    const codeTypeList = (codeType: IcdCodeType[] | undefined) => {
        if (codeType && length(codeType)) {
          return codeType.map((code, index) => ({
            label: code.diagnosisType,
            value: code.diagnosisTypeId,
            disabled: index === 0 && code.diagnosisTypeId === 1 ? true : false,
          }));
        }
        return [];
    };

    const removeTableRow = (rowIndex: any) => {
        setDuplicateIndexes((prev) =>
          prev.filter((i) => i !== Number(rowIndex)).map((i) => (i > Number(rowIndex) ? i - 1 : i))
        );
        const newItems = [...items];
        const removedItem = newItems.splice(rowIndex, 1);
        if (removedItem && removedItem[0].diagnosisTypeId === 1) {
          setRemoveFlag(true);
        } else {
          setRemoveFlag(false);
        }
        setItems(items.splice(rowIndex, rowIndex));
        remove(Number(rowIndex));
    };

    const handleAddOperation = () => {
        const newIcdCode = {
            diagnosisTypeId:
            items.length > 0 ? ICD10CodeType.SECONDARY : ICD10CodeType.PRIMARY,
            diagnosisCode: "",
            diagnosisCodeDescription: "",
            icdDiagnosisCodeId: null,
            diagnosisType: items.length > 0 ? 'SECONDARY' : 'PRIMARY',
        };
        if (items.length <= 5) {
          setItems((items) => [...items, newIcdCode]);
          append({
            diagnosisTypeId:
              items.length > 0 ? ICD10CodeType.SECONDARY : ICD10CodeType.PRIMARY,
            diagnosisCode: "",
            diagnosisCodeDescription: "",
            icdDiagnosisCodeId: null,
            diagnosisType: items.length > 0 ? 'SECONDARY' : 'PRIMARY',
          });
        }
        setRemoveFlag(false);
      };
    
    return (
        <Grid size={{xs:12}} sx={SummaryItemContainer}>
            <Grid container sx={flexAllEnd}>
                <Grid size={{xs:9}} p={"0 0 0 1rem"}>
                    <Box sx={flexAlignCentre}>
                        <Typography
                            variant="h6"
                            fontWeight={fontWeight[5]}
                            color={colors.fonts[20]}
                        >
                            {ICD10Title}
                        </Typography>
                        {formState && (
                            <Typography variant="subtitle1" color={colors.red[100]} m={"0 0.4rem 0 0.2rem"}>
                                *
                            </Typography>
                        )}
                        <Divider
                            orientation="vertical"
                            variant="fullWidth"
                            flexItem
                        />
                        <Typography
                            variant="body1"
                            color={colors.black[5]}
                            fontWeight={fontWeight[2]}
                            sx={ReauthOpacity}
                        >
                            {ICD10Text}
                        </Typography>
                    </Box>
                    {formState && (
                        <Typography variant="subtitle2" color={colors.red[100]} pt={"0.5rem"}>
                            {getValue(formState, "errors.diagnosisCode.root.message", null)}
                        </Typography> 
                    )}
                </Grid>
                <Grid size={{xs:3}} p={"0 0 0 3rem"}>
                    <Grid container>
                        <Grid size={{xs:1.5}} pt={"0.4rem"}>
                            <FaGripLinesIcon />
                        </Grid>
                        <Grid size={{xs:5.5}} pt={"0.6rem"}>
                        <Typography
                            variant="subtitle2"
                            fontWeight={fontWeight[3]}
                            color={colors.fonts[5]}
                            sx={ReauthOpacity}
                        >
                            {" "}
                            Drag to reorder
                        </Typography>
                        </Grid>
                        <Grid size={{xs:5}}>
                        {items.length >= 5 ? (
                            <Button
                                variant="contained" sx={NextStepButton}
                                disabled
                                data-testid="icd10-add-button"
                            >
                            ADD
                            </Button>
                            ) : (
                                <Button
                                    variant="contained"
                                    sx={NextStepButton}
                                    onClick={handleAddOperation}
                                    data-testid="icd10-add-button"
                                >
                                    ADD
                                </Button>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Grid container>
                <Grid size={{xs:12}} pt={"1rem"}>
                    <List
                        ref={sortableListRef}
                        values={items}
                        lockVertically={true}
                        onChange={({ oldIndex, newIndex }: any) =>
                            handleArrayMove(oldIndex, newIndex)
                        }
                        renderList={({ children, props, isDragged }: any) => (
                        <Table
                            style={{
                            cursor: isDragged ? "grabbing" : undefined,
                            }}
                        >
                            <TableHead sx={TableHeadStyle}>
                            <TableRow>
                                <TableCell width="5%"></TableCell>
                                <TableCell width="20%">
                                    <Typography
                                        variant="h6"
                                        fontWeight={fontWeight[5]}
                                        color={colors.fonts[20]}
                                    >
                                        Add Diagnosis Code
                                    </Typography>                                    
                                </TableCell>
                                <TableCell width="45%">
                                    <Typography
                                        variant="h6"
                                        fontWeight={fontWeight[5]}
                                        color={colors.fonts[20]}
                                    >
                                        Code Description
                                    </Typography>
                                </TableCell>
                                <TableCell width="20%">
                                    <Typography
                                        variant="h6"
                                        fontWeight={fontWeight[5]}
                                        color={colors.fonts[20]}
                                    >
                                        Type:
                                    </Typography>                                    
                                </TableCell>
                                <TableCell width="10%" align={'center'}>
                                    <Typography
                                        variant="h6"
                                        fontWeight={fontWeight[5]}
                                        color={colors.fonts[20]}
                                    >
                                        Actions:
                                    </Typography>
                                </TableCell>
                            </TableRow>
                            </TableHead>
                            <tbody {...props}>{children}</tbody>
                        </Table>
                        )}
                        renderItem={({ props, isDragged, index }: any) => {
                        setTimeout(() => {
                            setDragged(isDragged);
                        }, 0)
                        const {key, ...Itemprops} = props;
                        const row = (
                            <TableRow
                                key={key}
                                {...Itemprops}
                                sx={
                                    items && items[Number(index)].diagnosisTypeId === 1
                                    ? tableCellColor
                                    : tableCellNoColor
                                }
                            >
                            <TableCell
                                onMouseDown={(event) => handleDraggableClick(event, index)}
                                style={{
                                cursor: "grab",
                                }}
                                width="5%"
                                height="6rem"
                            >
                                <FaGripLinesIcon />
                            </TableCell>
                            <TableCell
                                width="30%"
                                height="6rem"
                                sx={AutocompleteTabelCell}
                            >
                                <Box height="4.4rem">
                                <Controller
                                    render={({ field: { ref }, fieldState: { error } }) => {
                                    return (
                                        <>
                                        <Autocomplete
                                            id="combo-box-demo"
                                            onBlur={() => setDiseaseTypeResp([])}
                                            sx={AutocompleteUI}
                                            freeSolo
                                            slots={{popper: StyledPopperCheckBoxAutocomplete}}
                                            options={
                                            icdDiseaseTypeResp &&
                                                icdDiseaseTypeResp.length > 0
                                                ? icdDiseaseTypeResp
                                                : []
                                            }
                                            isOptionEqualToValue={(option, value) =>
                                            option.icdDiagnosisCodeId ===
                                            value.icdDiagnosisCodeId
                                            }
                                            filterOptions={(icdDiseaseTypeResp) =>
                                            icdDiseaseTypeResp
                                            }
                                            onInputChange={handleInputChange}
                                            renderInput={(params) => {
                                                params.inputProps.maxLength =
                                                    rules.icd10Code.maxLength.value;
                                                return (
                                                    <>
                                                        <FormInputFieldIcon
                                                            iconStart={<FaSearchIcon />}                                                            
                                                            elementRef={ref}
                                                            {...params}
                                                        />
                                                    </>
                                                );
                                            }}
                                            renderOption={(props, option) => {
                                            return (
                                                <ListItem
                                                    {...props}
                                                    key={option.diagnosisCode}
                                                    >
                                                    {option.diagnosisCode + " - "}
                                                    {option.diagnosisCodeDescription}
                                                </ListItem>
                                            );
                                            }}
                                            getOptionLabel={(option) =>
                                            typeof option === "string"
                                                ? option
                                                : option.diagnosisCode !== undefined
                                                ? diseaseCode(option.diagnosisCode)
                                                : ""
                                            }
                                            value={
                                            icdvalues &&
                                            icdvalues.diagnosisCode &&
                                            icdvalues.diagnosisCode[Number(index)]
                                            }
                                            onChange={(e, val) =>
                                            handleDiseaseTypeChange(
                                                e,
                                                val,
                                                `diagnosisCode.${index}.icdDiagnosisCodeId`,
                                                `diagnosisCode.${index}.diagnosisCode`,
                                                index
                                            )
                                            }
                                        />
                                        {duplicateIndexes.includes(Number(index)) ? (
                                            <FormHelperText error>{ICD10DuplicateError}</FormHelperText>
                                        ) : (
                                            error && <FormHelperText>{error.message}</FormHelperText>
                                        )}
                                        </>
                                    );
                                    }}
                                    name={`diagnosisCode.${index}.icdDiagnosisCodeId`}
                                    control={control}
                                    rules={rules.icd10Code}
                                />
                                </Box>
                            </TableCell>
                            <TableCell
                                width="45%"
                                height="6rem"
                                sx={DescriptionTabelCell}
                            >
                                <Box height="4.4rem">
                                <FormControl variant="standard" fullWidth>
                                    <FormInputText
                                        name={`diagnosisCode.${index}.diagnosisCodeDescription`}
                                        control={control}
                                                readOnly
                                                InputStyle={{ fontSize: 'medium' }}
                                    />
                                </FormControl>
                                </Box>
                            </TableCell>
                            <TableCell
                                width="10%"
                                height="6rem"
                                sx={CodeTypeTabelCell}
                            >
                                <Box sx={CodeTypeDropDownWrap}>
                                <FormControl variant="standard" fullWidth>
                                    <FormInputDropdown
                                        readOnly
                                        control={control}
                                        name={`diagnosisCode.${index}.diagnosisTypeId`}
                                        list={codeTypeList(codeType)}
                                        helper={rules.icd10CodeType}
                                    />
                                </FormControl>
                                </Box>
                            </TableCell>
                            <TableCell
                                width="10%" 
                                align={'center'}
                                height="6rem"
                                sx={RemoveRowDeleteButton}
                            >
                                <Button
                                    onClick={() => removeTableRow(index)}
                                    style={DeleteButton}
                                    data-testid={`icd10-delete-record-${index}`}
                                >
                                <FaRegTrashAltIcon />
                                </Button>
                            </TableCell>
                            </TableRow>
                        );
                        return isDragged ? (
                            <Table style={{ borderSpacing: 0 }}>
                            <TableBody>{row}</TableBody>
                            </Table>
                        ) : (
                            row
                        );
                        }}
                    />
                </Grid>
            </Grid>
        </Grid>
    );
}

export default ICD10;